import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMyListingRequests } from "@/hooks/useMyListingRequests";
import { RequestListingDialog } from "@/components/listingRequests/RequestListingDialog";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MATERIAL_LABELS, QUALITY_LABELS, type MaterialType, type QualityTier } from "@/types";
import { compareLibraryRows, filterLibrary, toLibraryReports, type LibraryFilters, type LibraryReport } from "@/lib/soilLibrary";
import { verdictLabel } from "@/lib/measurementSummary";

const TIERS = Object.keys(QUALITY_LABELS) as QualityTier[];
const MATERIALS = Object.keys(MATERIAL_LABELS) as MaterialType[];

function ReportDetail({ r }: { r: LibraryReport }) {
  const [tier, setTier] = useState<QualityTier>(r.nyTier ?? "unrestricted");
  const rows = compareLibraryRows(r, tier);
  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Compare against</span>
        <Select value={tier} onValueChange={(v) => setTier(v as QualityTier)}>
          <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{TIERS.filter((t) => t !== "restricted").map((t) => <SelectItem key={t} value={t}>{QUALITY_LABELS[t]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No measured values recorded for this report.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr><th className="py-1 pr-3">Parameter</th><th className="pr-3">Value</th><th className="pr-3">NY limit</th><th className="pr-3">NY</th><th className="pr-3">NJ limit</th><th>NJ</th></tr>
            </thead>
            <tbody>
              {rows.map((m, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1 pr-3">{m.standardName ?? m.name}</td>
                  <td className="pr-3">{m.nonDetect ? "ND" : m.value} {m.unit}</td>
                  <td className="pr-3">{m.nyLimit ?? "—"}</td>
                  <td className="pr-3">{m.screening && !m.nonDetect ? "Staff review" : verdictLabel(m.ny, m.nyRatio)}</td>
                  <td className="pr-3">{m.njLimit ?? "—"}</td>
                  <td>{m.screening && !m.nonDetect ? "Staff review" : m.njLimit == null && !m.nonDetect ? "No NJ standard" : verdictLabel(m.nj, m.njRatio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SoilLibrary() {
  const { user, loading } = useAuth();
  const [f, setF] = useState<LibraryFilters>({ tier: "all", passes: "any", state: "all", material: "all", radiusMiles: null, maxValue: null });
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["soil-library"],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_library_reports");
      if (error) throw error;
      return toLibraryReports(data ?? []);
    },
  });
  const list = useMemo(() => filterLibrary(data ?? [], f), [data, f]);
  const roles = useQuery({
    queryKey: ["library-buyer", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user!.id);
      return (data ?? []).some((r) => r.role === "buyer");
    },
  });
  const isBuyer = roles.data === true;
  const myRequests = useMyListingRequests(isBuyer);
  const openIds = useMemo(() => new Set((myRequests.data ?? []).filter((q) => q.status === "open").map((q) => q.labReportId)), [myRequests.data]);
  const set = (patch: Partial<LibraryFilters>) => setF((p) => ({ ...p, ...patch }));

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 container py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Soil data library</h1>
          <p className="text-muted-foreground">Staff-confirmed lab results that sellers have chosen to share. Original lab files stay private.</p>
        </div>
        {!loading && !user ? (
          <Card><CardContent className="p-6 space-y-3">
            <p>Sign in to search the soil data library.</p>
            <Button asChild><Link to="/login?next=%2Flibrary">Sign in</Link></Button>
          </CardContent></Card>
        ) : (
          <>
            <Card><CardContent className="p-4 grid gap-3 md:grid-cols-4">
              <div><Label>Parameter</Label><Input placeholder="e.g. Lead" value={f.parameter ?? ""} onChange={(e) => set({ parameter: e.target.value })} /></div>
              <div><Label>Value at or below (mg/kg)</Label><Input type="number" min={0} value={f.maxValue ?? ""} onChange={(e) => set({ maxValue: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              <div><Label>Quality tier</Label>
                <Select value={f.tier} onValueChange={(v) => set({ tier: v as QualityTier | "all" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Any tier</SelectItem>{TIERS.map((t) => <SelectItem key={t} value={t}>{QUALITY_LABELS[t]}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Passes</Label>
                <Select value={f.passes} onValueChange={(v) => set({ passes: v as LibraryFilters["passes"] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="ny">NY</SelectItem><SelectItem value="nj">NJ</SelectItem><SelectItem value="both">NY and NJ</SelectItem></SelectContent>
                </Select></div>
              <div><Label>Material</Label>
                <Select value={f.material} onValueChange={(v) => set({ material: v as MaterialType | "all" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Any material</SelectItem>{MATERIALS.map((m) => <SelectItem key={m} value={m}>{MATERIAL_LABELS[m]}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>State</Label>
                <Select value={f.state} onValueChange={(v) => set({ state: v as LibraryFilters["state"] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">NY and NJ</SelectItem><SelectItem value="NY">NY</SelectItem><SelectItem value="NJ">NJ</SelectItem></SelectContent>
                </Select></div>
              <div><Label>ZIP code</Label><Input inputMode="numeric" maxLength={5} placeholder="e.g. 10001" value={f.zip ?? ""} onChange={(e) => set({ zip: e.target.value.replace(/\D/g, "") })} /></div>
              <div><Label>Within (miles, estimated)</Label><Input type="number" min={1} placeholder="Exact ZIP match" value={f.radiusMiles ?? ""} onChange={(e) => set({ radiusMiles: e.target.value === "" ? null : Number(e.target.value) })} /></div>
            </CardContent></Card>

            {isLoading ? <Skeleton className="h-40" /> : error ? (
              <p className="text-destructive">Could not load the library: {(error as Error).message}</p>
            ) : list.length === 0 ? (
              <p className="text-muted-foreground">{(data ?? []).length === 0 ? "No reports have been published to the library yet." : "No reports match these filters."}</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{list.length} report{list.length === 1 ? "" : "s"}</p>
                {list.map((r) => (
                  <Card key={r.id}><CardContent className="p-4 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold">{r.title}</h2>
                      <Badge variant="outline" className="gap-1"><ShieldCheck className="h-3 w-3" /> Staff confirmed</Badge>
                      <Badge variant={r.passesNy ? "default" : "destructive"}>NY {r.passesNy ? "pass" : "fail"}</Badge>
                      <Badge variant={r.passesNj ? "default" : "destructive"}>NJ {r.passesNj ? "pass" : "fail"}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {[r.materialType && MATERIAL_LABELS[r.materialType], r.nyTier && `NY tier: ${QUALITY_LABELS[r.nyTier]}`, [r.city, r.state, r.zip].filter(Boolean).join(", "), r.labName, r.reportDate && new Date(r.reportDate).toLocaleDateString()].filter(Boolean).join(" · ")}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? "Hide values" : `View ${r.measurements.length} values`}</Button>
                      {r.listingId && <Button size="sm" variant="ghost" asChild><Link to={`/marketplace/${r.listingId}`}>View listing</Link></Button>}
                      {isBuyer && !r.listingId && (openIds.has(r.id)
                        ? <Badge variant="secondary">Requested</Badge>
                        : <RequestListingDialog reportId={r.id} title={r.title} />)}
                    </div>
                    {open === r.id && <ReportDetail r={r} />}
                  </CardContent></Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
